import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";

const SelectStatusTags = ({
  //   tag = "Active",
  size = "md",
  variant = "contained",
  icon = false,
  className = "",
  options = [
    { label: "Active", value: 1 },
    { label: "Inactive", value: 2 },
  ],
  value, //{ label: "Active", value: 1 }
  onChange = () => {},
}) => {
  const [currentTag, setCurrentTag] = useState(value || options[0]);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownContainerRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        (dropdownRef.current && dropdownRef.current.contains(event.target)) ||
        (buttonRef.current && buttonRef.current.contains(event.target)) ||
        (dropdownContainerRef.current &&
          dropdownContainerRef.current.contains(event.target))
      ) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const tagStyles = {
    success: {
      contained: "bg-success-50 text-success",
      outlined: "border border-success text-success",
      text: "text-success",
    },
    warning: {
      contained: "bg-warning-100 text-warning",
      outlined: "border border-warning text-warning",
      text: "text-warning",
    },
    danger: {
      contained: "bg-danger-50 text-danger",
      outlined: "border border-danger text-danger",
      text: "text-danger",
    },
    black: {
      contained: "bg-black text-white",
      outlined: "border border-black text-black",
      text: "text-black",
    },
    gray: {
      contained: "bg-gray-100 text-gray-400",
      outlined: "border border-gray-400 text-gray-400",
      text: "text-gray-400",
    },
    info: {
      contained: "bg-info-100 text-info-400",
      outlined: "border border-info-400 text-info-400",
      text: "text-info-400",
    },
    draft: {
      contained: "bg-draft-100 text-draft-700",
      outlined: "border border-draft-600 text-draft-600",
      text: "text-draft-600",
    },
    invoiced: {
      contained: "bg-invoiced-100 text-invoiced-700",
      outlined: "border border-invoiced-500 text-invoiced-500",
      text: "text-invoiced-600",
    },
  };

  const tagStyleMap = {
    Active: "success",
    Submitted: "info",
    Approved: "success",
    Draft: "draft",
    "On hold": "warning",
    Rejected: "danger",
    Exit: "danger",
    Inactive: "black",
    "Job Inactive": "gray",
    Invoiced: "invoiced",
    Unknown: "gray",
    Blocked: "danger",
  };

  const dotColorMap = {
    Active: "bg-success",
    Submitted: "bg-info",
    Approved: "bg-success",
    Draft: "bg-draft",
    "On hold": "bg-warning",
    Rejected: "bg-danger",
    Blocked: "bg-danger",
    Exit: "bg-danger",
    Inactive: "bg-warning",
    "Job Inactive": "bg-gray-500",
    Invoiced: "bg-invoiced",
    Unknown: "bg-gray-400",
  };

  const styleCategory = tagStyleMap[currentTag.label] || "gray";
  const statusType = currentTag.label || "Unknown";

  const sizeStyles = {
    xs: "text-[10px] leading-5 px-2.5",
    "xs-sm": "text-[10px] leading-5 px-2.5",
    sm: "text-[12px] leading-6 px-2.5",
    md: "text-[14px] leading-7 px-4",
    lg: "text-[16px] leading-8 px-4",
    xl: "text-[18px] leading-9 px-4",
  };
  const dotSizeStyles = {
    xs: "w-1 h-1",
    "xs-sm": "w-1 h-1",
    sm: "w-1 h-1",
    md: "w-1.5 h-1.5",
    lg: "w-2 h-2",
    xl: "w-3 h-3",
  };
  const iconSizeClasses = {
    xs: "!text-[12px]",
    "xs-sm": "!text-[14px]",
    sm: "!text-[14px]",
    md: "!text-[14px]",
    lg: "!text-[15px]",
    xl: "!text-[15px]",
  };
  const fontSizeClasses = {
    xs: "text-[12px]",
    "xs-sm": "text-[12px]",
    sm: "text-[12px]",
    md: "text-[14px]",
    lg: "text-[16px]",
    xl: "text-[18px]",
  };

  const handleSelect = (opt) => {
    setCurrentTag(opt);
    onChange(opt);
    setIsOpen(false);
  };

  // Position dropdown
  useLayoutEffect(() => {
    if (isOpen && buttonRef.current && dropdownContainerRef.current) {
      const updatePosition = () => {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const dropdownRect =
          dropdownContainerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        const spaceBelow = viewportHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;

        const showBelow =
          spaceBelow >= dropdownRect.height || spaceBelow >= spaceAbove;

        let left = buttonRect.left;
        if (left + dropdownRect.width > viewportWidth) {
          left = Math.max(0, viewportWidth - dropdownRect.width);
        }

        let top;
        if (showBelow) {
          top = buttonRect.bottom + 4;
          if (top + dropdownRect.height > viewportHeight) {
            top = viewportHeight - dropdownRect.height - 8;
          }
        } else {
          top = buttonRect.top - dropdownRect.height - 4;
          if (top < 0) {
            top = 8;
          }
        }

        dropdownContainerRef.current.style.left = `${left}px`;
        dropdownContainerRef.current.style.top = `${top}px`;
        dropdownContainerRef.current.style.width = `${buttonRect.width}px`;
      };

      updatePosition();

      const handleScroll = () => requestAnimationFrame(updatePosition);
      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleScroll);

      return () => {
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", handleScroll);
      };
    }
  }, [isOpen]);

  return (
    <div className="inline-block" ref={dropdownRef}>
      <div
        ref={buttonRef}
        className={`relative rounded-md flex justify-between items-center gap-1 box-border cursor-pointer ${
          tagStyles[styleCategory][variant]
        } ${sizeStyles[size] || sizeStyles.md} ${className}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-1">
          {icon && (
            <div
              className={`rounded-full ${dotColorMap[statusType]} ${dotSizeStyles[size]}`}
            />
          )}
          {currentTag.label}
        </div>
        <span
          className={`material-icons transition-transform ${iconSizeClasses[size]} ml-1 flex-shrink-0`}
        >
          {isOpen ? "expand_less" : "expand_more"}
        </span>
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownContainerRef}
            style={{ position: "fixed", zIndex: 55 }}
            className="bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          >
            <ul className="p-2 space-y-1">
              {options.map((opt) => (
                <li
                  key={opt.value}
                  className="flex items-center p-2 rounded cursor-pointer hover:bg-primary-50"
                  onClick={() => handleSelect(opt)}
                >
                  <span className={`flex-1 ${fontSizeClasses[size]}`}>
                    {opt.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>,
          document.body
        )}
    </div>
  );
};

export default SelectStatusTags;
